import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, GamepadDirectional, BarChart3, User, Lock, KeyRound, Puzzle, ScrollText, Wrench } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useAltezzaBarra } from '../../hooks/useAltezzaBarra';
import './Sidebar.css';

export type NavItem = 'home' | 'play' | 'puzzle' | 'arena' | 'tournaments' | 'leaderboard' | 'profile' | 'faq' | 'rules' | 'strumenti';

interface SidebarProps {
  activeItem: NavItem;
  onNavigate: (item: NavItem) => void;
  isAuthenticated: boolean;
  isGuest?: boolean;
  onLogout?: () => void;
  onLoginClick?: () => void;
  /** Render as a horizontal top bar instead of a vertical left rail. */
  horizontal?: boolean;
}

export default function Sidebar({
  activeItem,
  onNavigate,
  isAuthenticated,
  isGuest,
  onLogout,
  onLoginClick,
  horizontal = false,
}: SidebarProps) {
  const { t } = useTranslation();

  /*
   * La barra si misura da sola: e' l'unico posto che possiede questo elemento, e
   * i numeri dichiarati sono pavimenti, non misure (vedi il commento su
   * `min-height` piu' sotto in questo file).
   *
   * Una misura sola, e solo DOVE LA BARRA STA IN BASSO: `--altezza-barra`,
   * quanto e' alta davvero. Il foglio di «Gioca» ci si posa sopra, e il fondo
   * della pagina le deve stare fuori.
   *
   * Ce n'era una seconda per la colonna del desktop — `--centro-gioca`, da
   * `useCentroGioca` — e serviva a far nascere quella tendina all'altezza della
   * casella «Gioca», che e' la terza e si sposta quando le due sopra vanno a
   * capo. Ora la tendina nasce in cima, in linea con «Home», che e' la prima
   * casella e non si sposta mai: non c'era piu' niente da misurare, e l'hook e'
   * stato cancellato (FoglioGioca.css).
   */
  /* Un ref di CALLBACK, non `useRef`: `useAltezzaBarra` misura l'elemento, e un
     ref stabile non direbbe mai all'effetto che l'elemento e' cambiato. */
  const [barra, setBarra] = useState<HTMLElement | null>(null);
  useAltezzaBarra(barra);

  // Build nav items - profile only shown when authenticated
  /*
   * `shortLabel`: l'etichetta della barra in basso, dove ogni voce ha un quinto
   * dello schermo — 72px a 360px — e una parola piu' lunga di una decina di
   * lettere non si stringe: allarga la sua casella e spinge le altre fuori
   * dallo schermo. Chi ce l'ha mostra quella sul telefono e quella lunga sul
   * desktop, dove la colonna e' larga; lo scambio lo fa il CSS, e il `title`
   * del bottone dice comunque il nome per intero.
   *
   * Oggi nessuna voce ne ha bisogno — l'unica che ce l'aveva era «Arene/Tornei»,
   * e quella porta e' finita nel foglio di «Gioca» — ma il meccanismo resta:
   * e' la rete per la prossima etichetta lunga, e le sue guardie stanno in
   * `barra-in-basso-360.test.ts`.
   */
  const navItems: { id: NavItem; icon: ReactNode; label: string; shortLabel?: string; requiresAuth: boolean }[] = [
    /* `Home` in tutte e otto le lingue, come l'etichetta degli strumenti piu' in
       basso e per la stessa ragione: la casella e' da 72px, e le traduzioni
       lunghe non ci stavano. Il nome tradotto serviva anche alla briciola dei
       dati strutturati, e quella ha ora una chiave sua (`seo.breadcrumbHome`):
       in un risultato di ricerca la parola della lingua vale piu' della
       brevita'. */
    { id: 'home', icon: <Home size={20} />, label: t('nav.home'), requiresAuth: false },
    { id: 'rules', icon: <ScrollText size={20} />, label: t('nav.rules'), requiresAuth: false },
    // Puzzle accessible only from DailyPuzzleCard in dashboard for now
    // { id: 'puzzle', icon: <Puzzle size={20} />, label: t('puzzle.title'), requiresAuth: false },
    /*
     * «Gioca», ed e' al CENTRO della barra — il punto che il pollice raggiunge
     * per primo, e finora l'unica cosa che la barra non sapeva fare.
     *
     * Al centro ci arriva da se', senza contare le caselle: le due voci qui
     * sopra ci sono sempre, e quello che segue — Strumenti e Profilo per chi e'
     * registrato, Accedi e Lingua per tutti gli altri (renderizzati sotto il
     * `.map()`) — sono sempre due. Quindi la barra e' di cinque caselle in ogni
     * stato e questa e' la terza. E' la stessa griglia per cui il CSS e' stato
     * scritto, «cinque voci, un quinto di schermo ciascuna»: a 360px sono 72px
     * per casella, la misura di prima.
     *
     * Non naviga: apre il foglio con le sei porte del gioco (FoglioGioca.tsx),
     * fra cui i tornei, che prima erano una voce di barra a se'. Sul desktop il
     * foglio non c'e' e si va dritti al lobby — lo decide `handleNavigate`, che
     * e' l'unico posto che sa in che schermata siamo.
     *
     * Il gamepad, e NON `Swords`: le spade incrociate in tutta l'app vogliono
     * dire «arena», ed e' una convenzione scritta due volte apposta perche'
     * nessuno la rompa per distrazione (utils/challenge-icons.tsx,
     * CustomTableOverlay.tsx). Qui l'arena e' una delle sei porte dietro
     * questa voce: darle la sua icona avrebbe detto che la voce E' l'arena.
     *
     * Venti come tutte le altre. Era 22 per riempire la pastiglia crema che
     * stava dietro l'icona; tolta quella — il fondo pieno in questa barra vuol
     * dire «sei qui», e non poteva volerne dire due — il 22 era un residuo, e
     * l'unica voce piu' grande delle altre senza una ragione.
     *
     * Del gamepad si vede la croce direzionale (`GamepadDirectional`) e non il
     * pad intero (`Gamepad2`). Tolta la pastiglia, ogni icona si misura sulle
     * vicine, e il tracciato del controller e' l'unico schiacciato: misurati
     * con `getBBox` nella casella da 24, casa fa 18x19, la pergamena 20x18, la
     * chiave 20x20 — il controller 20x14, cioe' 12px di altezza resa contro i
     * 15-17 delle altre. La croce fa 20x20 come la chiave: larga uguale, e
     * alta quanto tutte.
     */
    { id: 'play', icon: <GamepadDirectional size={20} />, label: t('nav.play'), requiresAuth: false },
    /*
     * La voce «Tornei» non e' piu' qui: la sua porta e' una riga del foglio di
     * «Gioca», e li' ha conservato il suo gate — la modalita' sviluppatore, i
     * dieci tocchi sul copyright del footer. Finche' i tornei non si riempiono,
     * una lista vuota non si offre a chi passa.
     *
     * `getActiveNavItem` (App.tsx) accende 'play' su tutte le schermate delle
     * gare e dei problemi: la voce accesa e' la porta da cui ci si e' entrati.
     */
    // { id: 'tournaments', icon: <Trophy size={20} />, label: t('nav.tournaments'), requiresAuth: false },
    // { id: 'leaderboard', icon: <BarChart3 size={20} />, label: t('nav.leaderboard'), requiresAuth: false },
    /*
     * «Strumenti» e Profilo: solo per chi ha un conto vero, non ospite. Gli
     * strumenti stanno fra Regole e Profilo — cioe' dove stava l'Editor, che di
     * questo menu e' la prima riga.
     *
     * Il gate e' quello di prima e vale per la stessa ragione, spostata di un
     * passo: non e' piu' «comporre una posizione richiede un conto», e' che la
     * prima riga di questo menu lo richiede. Rispecchiato in `vaiAllaVoce`
     * (App.tsx), che e' dove la riga arriva.
     *
     * Non naviga: apre il foglio con i tre attrezzi (FoglioStrumenti.tsx), come
     * «Gioca» apre il suo. Il conto resta di CINQUE caselle in ogni stato
     * d'accesso — un menu al posto di una porta non aggiunge una cella — quindi
     * «Gioca» e' ancora la terza, che e' la griglia per cui il CSS e' scritto.
     *
     * `Wrench` perche' in tutta la web non e' usata da nessun altro: `Settings2`
     * dice gia' «impostazioni della partita» nel foglio del lobby, e `SquarePen`
     * resta l'icona della sola riga Editor dentro il menu.
     *
     * ⚠️ L'ETICHETTA HA UN BUDGET: ~72px, la casella a 360px, e il desktop
     * non la salva — la colonna di sinistra e' larga 80px, la stessa misura. La
     * barra non taglia e non manda a capo: sborda e basta. Misurate nel browser,
     * tre delle otto traduzioni arrivavano a 82, 95 e 100px, cioe' addosso alla
     * voce vicina, e si erano dovute rimpiazzare con una parola che le altre
     * cinque non avevano.
     *
     * Adesso la chiave dice `Tools` in tutte e otto, per scelta e non per
     * ripiego: una parola corta, uguale in ogni lingua, che si legge sul
     * telefono — e nessun budget da ri-misurare quando se ne aggiunge una. La
     * parola della lingua non e' sparita: vive nel titolo del foglio che questa
     * voce apre (`strumenti.titoloFoglio`), dove lo spazio non manca.
     */
    ...(isAuthenticated && !isGuest
      ? [
          { id: 'strumenti' as NavItem, icon: <Wrench size={20} />, label: t('nav.strumenti'), requiresAuth: false },
          { id: 'profile' as NavItem, icon: <User size={20} />, label: t('nav.profile'), requiresAuth: false },
        ]
      : []),
  ];

  return (
    <aside ref={setBarra} className={`sidebar${horizontal ? ' sidebar-horizontal' : ''}`}>
      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.map((item) => {
          const isDisabled = item.requiresAuth && !isAuthenticated;
          const isActive = activeItem === item.id;

          return (
            <button
              key={item.id}
              className={`sidebar-nav-item ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
              /*
               * L'aggancio del foglio di stile, e vale per OGNI voce, non solo
               * per «Gioca»: un `data-nav` generico non e' un caso speciale
               * infilato nel render, ed e' li' anche per la prossima voce che
               * avra' bisogno di un trattamento suo.
               */
              data-nav={item.id}
              onClick={() => { if (!isDisabled) {  onNavigate(item.id); } }}
              title={isDisabled ? t('nav.loginRequired') : item.label}
              disabled={isDisabled}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              <span className={`sidebar-nav-label${item.shortLabel ? ' sidebar-nav-label-lunga' : ''}`}>
                {item.label}
              </span>
              {item.shortLabel && (
                <span className="sidebar-nav-label sidebar-nav-label-corta">{item.shortLabel}</span>
              )}
              {isDisabled && <span className="sidebar-lock"><Lock size={14} /></span>}
            </button>
          );
        })}

        {/* Login button in nav (when not authenticated) */}
        {(!isAuthenticated || isGuest) && onLoginClick && (
          <button
            className="sidebar-nav-item sidebar-login-nav"
            onClick={() => {  onLoginClick!(); }}
            title={t('nav.signIn')}
          >
            <span className="sidebar-nav-icon"><KeyRound size={20} /></span>
            <span className="sidebar-nav-label">{t('nav.signIn')}</span>
          </button>
        )}

        {/* Language switcher — sits alongside Home / Rules / Sign In, hidden once logged in */}
        {(!isAuthenticated || isGuest) && <LanguageSwitcher />}
      </nav>
    </aside>
  );
}
